import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { statsData, faqData } from '@/mocks/homeData';
import SeoStructuredData from '@/components/base/SeoStructuredData';

export default function AboutPage() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setContactStatus('success');
      form.reset();
      setTimeout(() => setContactStatus('idle'), 3000);
      return;
    }

    try {
      const payload = new URLSearchParams();
      payload.append('name', formData.get('name') as string);
      payload.append('email', formData.get('email') as string);
      payload.append('message', formData.get('message') as string);

      const res = await fetch('http://thinkbu.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        form.reset();
        setTimeout(() => setContactStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后再试';
        if (serverMsg.includes('spam')) {
          setContactStatus('success');
          form.reset();
        } else {
          setContactError(serverMsg);
          setContactStatus('error');
        }
      }
    } catch {
      setContactError('网络错误，请检查网络后重试');
      setContactStatus('error');
    }
  };

  return (
    <>
      <SeoStructuredData type="WebPage" data={{ name: '关于我们 - 土豪漫画网', url: 'http://thinkbu.com/about', description: '土豪漫画网品牌介绍、用户口碑、联系方式与常见问题解答' }} />
      <SeoStructuredData
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: 'http://thinkbu.com/' },
            { name: '关于我们', url: 'http://thinkbu.com/about' },
          ],
        }}
      />
      <SeoStructuredData
        type="FAQPage"
        data={{
          questions: faqData.map((item) => ({ question: item.question, answer: item.answer })),
        }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          {/* Breadcrumb */}
          <nav aria-label="面包屑导航" className="flex items-center gap-2 text-sm text-foreground-500 mb-8">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </nav>

          {/* Hero */}
          <section className="mb-16">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">关于土豪漫画</h1>
            <p className="text-lg text-foreground-600 max-w-3xl leading-relaxed">
              土豪漫画网是一个专注于<strong>免费漫画下拉式</strong>阅读的中文漫画平台，
              致力于为漫画爱好者提供最优质的<strong>高清韩漫</strong>在线观看体验。
            </p>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { value: `${(statsData.totalComics / 10000).toFixed(1)}万+`, label: '漫画资源' },
              { value: `${(statsData.dailyUpdates).toLocaleString()}+`, label: '每日更新' },
              { value: `${(statsData.registeredUsers / 10000).toFixed(0)}万+`, label: '注册用户' },
              { value: `${(statsData.totalViews / 100000000).toFixed(0)}亿+`, label: '累计阅读' },
            ].map((stat) => (
              <div key={stat.label} className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-2">{stat.value}</div>
                <div className="text-sm text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </section>

          {/* Brand Story */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">品牌故事</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p>
                  土豪漫画网成立于2020年，由一群热爱漫画的年轻人共同创建。我们发现许多漫画平台存在加载慢、
                  广告多、画质差等问题，于是决定打造一个以用户体验为核心的<strong>免费漫画</strong>阅读平台。
                </p>
                <p>
                  我们首创<strong>下拉式漫画</strong>阅读模式，让用户可以像刷社交媒体一样流畅地阅读漫画，
                  无需频繁点击翻页按钮。这一创新体验获得了广大漫友的一致好评。
                </p>
                <p>
                  平台专注于<strong>韩国漫画</strong>资源，与多家韩国漫画平台达成合作，
                  为用户提供最快、最全的<strong>高清韩漫</strong>内容。我们的<strong>人气榜</strong>
                  每天实时更新，帮助用户发现最热门的漫画作品。
                </p>
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20cozy%20creative%20studio%20workspace%20with%20large%20monitors%20displaying%20webtoon%20art%2C%20warm%20lighting%2C%20wooden%20desk%2C%20plants%2C%20team%20collaboration%20atmosphere%2C%20creative%20agency%20vibe%2C%20clean%20minimalist%20design%2C%20soft%20natural%20light&width=800&height=600&seq=about-story-01&orientation=landscape"
                  alt="土豪漫画团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8">常见问题</h2>
            <div className="max-w-3xl space-y-3">
              {faqData.map((faq, idx) => (
                <div key={idx} className="rounded-xl border border-background-200/70 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left bg-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <span className="font-medium text-foreground-900 text-sm pr-4">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-foreground-500 shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 py-4 bg-background-50 border-t border-background-200/70">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { text: '下拉式阅读体验真的太流畅了，一口气看完一整部漫画都不觉得累！', author: '小李', role: '资深漫友' },
                { text: '更新速度超快，人气榜上的韩漫基本都是最新的，强烈推荐！', author: '漫画达人', role: '3年老用户' },
                { text: '画质清晰，分类全面，而且完全免费，是我用过最好的漫画平台。', author: '追漫少女', role: '忠实粉丝' },
                { text: '每天通勤路上必刷，下拉模式单手操作超方便，已经成为生活必需品了。', author: '上班族阿强', role: '日常用户' },
                { text: '作为一个漫画博主，土豪漫画的资源量和更新速度真的没得说，强烈安利！', author: '漫评博主', role: '行业推荐' },
                { text: '朋友推荐的，用了就回不去了。界面简洁没有乱七八糟的广告，体验真的很棒。', author: '新用户小王', role: '新晋漫友' },
              ].map((item, idx) => (
                <div key={idx} className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <p className="text-foreground-700 leading-relaxed mb-4 text-sm">&ldquo;{item.text}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-accent-700">{item.author[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{item.author}</div>
                      <div className="text-xs text-foreground-500">{item.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</div>
                    <p className="text-sm text-foreground-600">hello@thinkbu.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-customer-service-2-line text-accent-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900 mb-1">客服微信</div>
                    <p className="text-sm text-foreground-600">tuhaomanhua_kefu</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-secondary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900 mb-1">工作时间</div>
                    <p className="text-sm text-foreground-600">周一至周日 9:00 - 22:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-team-line text-accent-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900 mb-1">商务合作</div>
                    <p className="text-sm text-foreground-600">hello@thinkbu.com</p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">发送消息</h3>
                <form className="space-y-4" id="contact-form" onSubmit={handleContactSubmit}>
                  <div>
                    <input type="text" name="name" placeholder="您的姓名" className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors" required />
                  </div>
                  <div>
                    <input type="email" name="email" placeholder="您的邮箱" className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors" required />
                  </div>
                  <div>
                    <textarea name="message" placeholder="请输入您的消息..." rows={4} maxLength={500} className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none" required></textarea>
                  </div>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <button type="submit" className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    发送消息
                  </button>
                  {contactStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">消息已发送成功！我们会尽快回复您。</p>
                  )}
                  {contactStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{contactError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}